import formatDate from "date-fns/format";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
TimeAgo.addDefaultLocale(en);
// TODO: Need to handle internationalization
const timeAgo = new TimeAgo("en-US");

export function formatHourAmPm(h: number): string {
  return `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;
}

export const convertDateToString = (date: Date): string => {
  return formatDate(date, "MM/dd/yyyy");
};

export const convertTimeToString = (date: Date): string => {
  return formatDate(date, "hh:mm a");
};

export const convertDateTimeToString = (date: Date): string => {
  return convertDateToString(date) + " " + convertTimeToString(date);
};

export const formatSessionDateRange = (startDate: Date, endDate: Date): string => {
  const startDateString = convertDateToString(startDate);
  const endDateString = convertDateToString(endDate);

  if (startDateString === endDateString) {
    // If the end date is same as the start date, it will return just one date string
    // e.g: formatSessionDateRange("11/23/2023 03:35:23", "11/23/2023 03:45:33") = "11/23/2023"
    return startDateString;
  }

  const startNextDate = new Date(startDate);
  startNextDate.setDate(startDate.getDate() + 1);

  const startNextDateString = convertDateToString(startNextDate);

  if (startNextDateString === endDateString) {
    // If the end date is the next day of the start date, it will return just start date string and 'next day' as end date string
    // e.g: formatSessionDateRange("11/23/2023 23:35:23", "11/24/2023 01:45:33") = "11/23/2023 - next day"
    return `${startDateString} - next day`;
  }

  // other cases, return start and end date strings
  // e.g: formatSessionDateRange("11/23/2023 23:35:23", "11/25/2023 01:45:33") = "11/23/2023 - 11/25/2023"
  return `${startDateString} - ${endDateString}`;
};

export const formatDateAgo = (date: Date): string => {
  return timeAgo.format(date, "round-minute");
};

export const formatDaysOfWeek = (daysOfWeek: string[]): string => {
  return `${daysOfWeek[0].charAt(0).toUpperCase() + daysOfWeek[0].slice(1)} - ${
    daysOfWeek[daysOfWeek.length - 1].charAt(0).toUpperCase() + daysOfWeek[daysOfWeek.length - 1].slice(1)
  }`;
};

// Shared by every duration formatter below: splits a whole-second duration into hours and
// leftover minutes. `Math.floor(Math.floor(totalSeconds / 60) / 60)` is mathematically identical
// to `Math.floor(totalSeconds / 3600)` for non-negative integers, so formatters needing
// second-level precision (formatDurationMs) and ones that don't (formatDwellDuration) can share
// this without either losing precision or double-rounding.
const splitDurationSec = (totalSeconds: number): { hours: number; minutes: number } => {
  const totalMinutes = Math.floor(totalSeconds / 60);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
};

// formats: HH:MM:SS
export const formatDurationMs = (durationMs: number): string => {
  const totalSeconds = Math.round(durationMs / 1000);
  const { hours, minutes } = splitDurationSec(totalSeconds);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// Formats a dwell duration given in seconds as "X hr Y min", or just "Y min" when under an hour.
// e.g. 2700 -> "45 min", 5400 -> "1 hr 30 min", 7200 -> "2 hr 0 min"
export const formatDwellDuration = (durationSec: number): string => {
  const { hours, minutes } = splitDurationSec(durationSec);
  return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
};

export const formatTimeOfDay = (
  timeOfDayStartHours: number,
  timeOfDayStartMinutes: number,
  timeOfDayEndHours: number,
  timeOfDayEndMinutes: number,
): string => {
  const formatHour = (hour: number) => {
    const h = hour % 12 || 12;
    return h < 10 ? `0${h}` : `${h}`;
  };

  const formatMinute = (minute: number) => {
    return minute < 10 ? `0${minute}` : `${minute}`;
  };

  const startHour = formatHour(timeOfDayStartHours);
  const startMinute = formatMinute(timeOfDayStartMinutes);
  const endHour = formatHour(timeOfDayEndHours);
  const endMinute = formatMinute(timeOfDayEndMinutes);

  return `${startHour}:${startMinute} ${timeOfDayStartHours < 12 ? "AM" : "PM"} - ${endHour}:${endMinute} ${
    timeOfDayEndHours < 12 ? "AM" : "PM"
  }`;
};
